import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  EmailIcon,
  CallIcon,
} from '../../assets/Icons';
import { footerData } from '../../data/footerData';
import { useModal } from '../../hooks/useModal';

export const Footer = () => {
  const { openDonationModal } = useModal();
  return (
    <footer className='bg-footer-green text-white relative overflow-hidden'>
      <div className='absolute -right-48 top-1/2 -translate-y-1/2 h-[350px] w-[350px] opacity-80'>
        <StaticImage
          src='../../assets/images/si-logo-grey-big.png'
          alt='Seeds Impact logo background'
          className='h-full w-full object-contain object-left'
          placeholder='blurred'
        />
      </div>
      <div className='container mx-auto px-4 py-12'>
        {/* Main Footer Content */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8'>
          {/* Column 1: Logo, About Content, Social Icons */}
          <div className='lg:col-span-3'>
            <div className='mb-6'>
              {/* Logo */}
              <div className='flex justify-start items-start mb-4'>
                <StaticImage
                  src='../../assets/images/seeds-logo-white.png'
                  alt='Seeds Impact Logo'
                  className='w-48 object-contain'
                  placeholder='blurred'
                  layout='constrained'
                  width={192}
                />
              </div>

              {/* About Content */}
              <p className='text-sm text-footer-text font-inter-regular leading-relaxed mb-6'>
                {footerData.aboutContent}
              </p>

              {/* Social Icons */}
              <div className='flex space-x-3'>
                {footerData.socialLinks.map((social, index) => {
                  const IconComponent =
                    social.icon === 'FacebookIcon'
                      ? FacebookIcon
                      : social.icon === 'InstagramIcon'
                        ? InstagramIcon
                        : social.icon === 'LinkedinIcon'
                          ? LinkedinIcon
                          : FacebookIcon;

                  return (
                    <a
                      key={index}
                      href={social.href}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='w-8 h-8 bg-[#F8BD17] rounded-full flex items-center justify-center hover:bg-yellow-600 hover:text-[#F8BD17] transition-colors'
                      aria-label={social.platform}
                    >
                      <IconComponent className='w-4 h-4' fill='black' />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Column 2: About Us Links */}
          <div className='lg:col-span-2'>
            <div className='mb-6'>
              <h3 className='text-md font-semibold mb-1 w-fit font-satoshi-medium'>
                {footerData.aboutUs.title}
              </h3>
              <div className='h-0.5 w-12 bg-[#F8BD17] mb-3'></div>
              <ul className='space-y-2'>
                {footerData.aboutUs.links.map((link, index) => {
                  const isDonate = link.label.toLowerCase() === 'donate';
                  return (
                    <li key={index}>
                      {isDonate ? (
                        <button
                          type='button'
                          onClick={openDonationModal}
                          className='text-sm hover:text-yellow-400 transition-colors text-footer-text'
                        >
                          {link.label}
                        </button>
                      ) : (
                        <a
                          href={link.href}
                          className='text-sm hover:text-yellow-400 transition-colors text-footer-text'
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Column 3: Extra Links */}
          <div className='lg:col-span-2'>
            <div className='mb-6'>
              <h3 className='text-md font-semibold mb-1 w-fit font-satoshi-medium'>
                {footerData.extraLinks.title}
              </h3>
              <div className='h-0.5 w-12 bg-[#F8BD17] mb-3'></div>
              <ul className='space-y-2'>
                {footerData.extraLinks.links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className='text-sm hover:text-yellow-400 transition-colors text-footer-text'
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 4: Contact Us */}
          <div className='lg:col-span-2'>
            <div className='mb-6'>
              <h3 className='text-md font-semibold mb-1 w-fit font-satoshi-medium'>
                {footerData.contactInfo.title}
              </h3>
              <div className='h-0.5 w-12 bg-[#F8BD17] mb-3'></div>
              <div className='space-y-3'>
                {footerData.contactInfo.contacts.map((contact, index) => (
                  <div key={index} className='flex items-center space-x-3'>
                    {contact.type === 'email' ? (
                      <EmailIcon
                        className='w-4 h-4 flex-shrink-0'
                        fill='#F8BD17'
                      />
                    ) : (
                      <CallIcon
                        className='w-4 h-4 flex-shrink-0'
                        fill='#F8BD17'
                      />
                    )}
                    <a
                      href={contact.href}
                      className='text-sm hover:text-yellow-400 transition-colors text-footer-text'
                    >
                      {contact.value}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Column 5: Address Information */}
          <div className='lg:col-span-3'>
            <div className='space-y-6'>
              {/* Registered Address */}
              <div>
                <h3 className='text-md font-semibold mb-1 w-fit font-satoshi-medium'>
                  {footerData.addresses.registered.title}
                </h3>
                <div className='h-0.5 w-12 bg-[#F8BD17] mb-3'></div>
                <div className='flex items-start space-x-3'>
                  <p className='text-sm leading-relaxed text-footer-text'>
                    {footerData.addresses.registered.address}
                  </p>
                </div>
              </div>

              {/* Centre of Excellence */}
              <div>
                <h3 className='text-md font-semibold mb-1 w-fit font-satoshi-medium'>
                  {footerData.addresses.centreOfExcellence.title}
                </h3>
                <div className='h-0.5 w-12 bg-[#F8BD17] mb-3'></div>
                <div className='space-y-2'>
                  <div className='flex items-start space-x-3'>
                    <p className='text-sm leading-relaxed text-footer-text'>
                      {footerData.addresses.centreOfExcellence.address}
                    </p>
                  </div>
                  {footerData.addresses.centreOfExcellence.phone && (
                    <div className='flex items-center space-x-3'>
                      <a
                        href={`tel:${footerData.addresses.centreOfExcellence.phone.replace(/\D/g, '')}`}
                        className='text-sm hover:text-yellow-400 transition-colors text-footer-text'
                      >
                        Tel: {footerData.addresses.centreOfExcellence.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Section */}
      <div className='py-8 border-t border-[#A2A2A2]'>
        <div className='text-center'>
          <p className='text-xs text-[#A2A2A2]'>{footerData.copyright}</p>
        </div>
      </div>
    </footer>
  );
};
